import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import api from '../utils/api';

// Fallback used when /api/awards.php returns nothing (fresh install, dev with
// no admin uploads yet). Matches the original hardcoded set so the homepage
// never renders an empty awards strip.
const FALLBACK_AWARDS = Array.from({ length: 9 }, (_, i) => {
  const num = String(i + 1).padStart(2, '0');
  return {
    src: `/assets/awards/award-${num}.webp`,
    alt: `Mind Spa award or certificate ${i + 1}`,
    title: `Certificate ${num}`,
  };
});

function AwardsCertificates() {
  const [awards, setAwards] = useState(FALLBACK_AWARDS);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let active = true;
    api.getAwards()
      .then((data) => {
        const items = data && Array.isArray(data.items) ? data.items : [];
        if (active && items.length > 0) setAwards(items);
      })
      .catch(() => { /* keep fallback */ });
    return () => { active = false; };
  }, []);

  const AWARDS = awards;

  useEffect(() => {
    if (lightbox === null) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener('keydown', onKey);
    };
  }, [lightbox]);

  const next = () => setLightbox((i) => (i + 1) % AWARDS.length);
  const prev = () => setLightbox((i) => (i - 1 + AWARDS.length) % AWARDS.length);

  return (
    <section className="awards-section">
      <div className="container">
        <header className="awards-section__header">
          <p className="section-label">Recognition</p>
          <h2 className="section-title">Awards &amp; Certificates</h2>
          <p className="awards-section__lead">
            Internationally accredited certifications and recognitions earned by
            Prof (Dr) Manju Agrawal and the Mind Spa team over decades of clinical practice.
          </p>
        </header>

        <div className="awards-grid">
          {AWARDS.map((a, i) => (
            <button
              key={i}
              type="button"
              className="award-card"
              onClick={() => setLightbox(i)}
              aria-label={`Open ${a.alt} in full size`}
            >
              <div className="award-card__frame">
                <img src={a.src} alt={a.alt} width="800" height="1124" loading="lazy" decoding="async" />
              </div>
              <div className="award-card__caption">
                <span className="award-card__num">No. {String(i + 1).padStart(2, '0')}</span>
                <span className="award-card__expand" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 3 21 3 21 9"/>
                    <polyline points="9 21 3 21 3 15"/>
                    <line x1="21" y1="3" x2="14" y2="10"/>
                    <line x1="3" y1="21" x2="10" y2="14"/>
                  </svg>
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {lightbox !== null && ReactDOM.createPortal(
        <div className="awards-lightbox" onClick={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}>
          <button className="awards-lightbox__close" onClick={() => setLightbox(null)} aria-label="Close">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <button className="awards-lightbox__nav awards-lightbox__nav--prev" onClick={prev} aria-label="Previous">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <img src={AWARDS[lightbox].src} alt={AWARDS[lightbox].alt} className="awards-lightbox__img" />
          <button className="awards-lightbox__nav awards-lightbox__nav--next" onClick={next} aria-label="Next">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
          <div className="awards-lightbox__counter">{lightbox + 1} / {AWARDS.length}</div>
        </div>,
        document.body
      )}

      <style>{`
        .awards-section {
          padding: clamp(72px, 9vw, 110px) 0;
          background: #fff;
        }
        .awards-section__header {
          text-align: center;
          margin-bottom: 50px;
        }
        .awards-section__lead {
          font-family: var(--font-body);
          font-size: 16px;
          color: var(--text-body);
          line-height: 1.7;
          max-width: 640px;
          margin: 14px auto 0;
        }

        /* Grid — 3 across on desktop (9 items land as a clean 3·3·3) */
        .awards-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          justify-items: stretch;
        }

        /* Card */
        .award-card {
          display: flex;
          flex-direction: column;
          gap: 0;
          background: var(--bg-section);
          border: 1px solid var(--border-light);
          border-radius: 14px;
          overflow: hidden;
          padding: 0;
          cursor: pointer;
          font: inherit;
          text-align: left;
          transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
        }
        .award-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 40px rgba(35,30,15,0.08);
          border-color: var(--accent);
        }

        /* Frame — gives certificates a "mounted" look */
        .award-card__frame {
          position: relative;
          aspect-ratio: 4 / 3;
          background:
            linear-gradient(135deg, #fff 0%, #FAF6EC 100%);
          padding: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 1px solid var(--border-light);
          overflow: hidden;
        }
        .award-card__frame::before,
        .award-card__frame::after {
          content: '';
          position: absolute;
          inset: 6px;
          border: 1px solid var(--border-light);
          border-radius: 8px;
          pointer-events: none;
          transition: border-color 0.35s ease;
        }
        .award-card:hover .award-card__frame::before {
          border-color: var(--accent-light);
        }

        .award-card__frame img {
          max-width: 100%;
          max-height: 100%;
          width: auto;
          height: auto;
          object-fit: contain;
          display: block;
          filter: drop-shadow(0 2px 6px rgba(0,0,0,0.08));
          transition: transform 0.4s ease;
        }
        .award-card:hover .award-card__frame img {
          transform: scale(1.03);
        }

        /* Caption strip */
        .award-card__caption {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: #fff;
        }
        .award-card__num {
          font-family: var(--font-body);
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          color: var(--text-light);
        }
        .award-card__expand {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--accent-light);
          color: var(--accent);
          transition: background 0.25s ease, color 0.25s ease;
        }
        .award-card:hover .award-card__expand {
          background: var(--accent);
          color: #fff;
        }

        /* Lightbox */
        .awards-lightbox {
          position: fixed;
          inset: 0;
          background: rgba(20, 24, 26, 0.94);
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: clamp(16px, 4vw, 40px);
          animation: awardsFade 0.25s ease;
        }
        @keyframes awardsFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .awards-lightbox__img {
          max-width: 92%;
          max-height: 88vh;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 30px 80px rgba(0,0,0,0.6);
          background: #fff;
        }
        .awards-lightbox__close,
        .awards-lightbox__nav {
          position: absolute;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.25);
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease, transform 0.2s ease;
        }
        .awards-lightbox__close:hover,
        .awards-lightbox__nav:hover {
          background: rgba(255,255,255,0.22);
          transform: scale(1.06);
        }
        .awards-lightbox__close { top: 24px; right: 24px; }
        .awards-lightbox__nav--prev { left: 24px; top: 50%; transform: translateY(-50%); }
        .awards-lightbox__nav--next { right: 24px; top: 50%; transform: translateY(-50%); }
        .awards-lightbox__nav--prev:hover { transform: translateY(-50%) scale(1.06); }
        .awards-lightbox__nav--next:hover { transform: translateY(-50%) scale(1.06); }
        .awards-lightbox__counter {
          position: absolute;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%);
          color: rgba(255,255,255,0.85);
          font-family: var(--font-body);
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0.5px;
          padding: 6px 14px;
          background: rgba(0,0,0,0.4);
          border-radius: 999px;
          backdrop-filter: blur(8px);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .awards-grid { grid-template-columns: repeat(3, 1fr); }
        }
        @media (max-width: 720px) {
          .awards-grid { grid-template-columns: repeat(2, 1fr); gap: 14px; }
        }
        @media (max-width: 420px) {
          .awards-section { padding: 56px 0; }
          .award-card__frame { padding: 10px; }
          .awards-lightbox__close,
          .awards-lightbox__nav { width: 40px; height: 40px; }
          .awards-lightbox__close { top: 14px; right: 14px; }
          .awards-lightbox__nav--prev { left: 10px; }
          .awards-lightbox__nav--next { right: 10px; }
        }
      `}</style>
    </section>
  );
}

export default AwardsCertificates;
